import { useState, useEffect } from 'react'

export const useFetchBankDeposits = ({ marvarApi, dispatch, onLogout }) => {

    const defaultParams = {
        startDate: '',
        endDate: '',
        idbanco: null,
    }

    const [paramsToSearch, setParamsToSearch] = useState(defaultParams)
    const [dataBankDeposits, setDataBankDeposits] = useState([])
    const [loadingFetchBankDeposits, setLoadingFetchBankDeposits] = useState(false)

    const [execute, setExecute] = useState(false)
    const executeFetch = async () => setExecute(!execute)

    const resetFetch = async () => {
        setParamsToSearch(defaultParams)
        setDataBankDeposits([])
    }

    const fetchData = async () => {
        const token = localStorage.getItem('token')
        if (!token) return dispatch(onLogout())

        const _dataBody = {
            startDate: paramsToSearch.startDate,
            endDate: paramsToSearch.endDate,
            idbanco: paramsToSearch.idbanco,
        }

        const response = await marvarApi.post(`/bank/deposits`, _dataBody)
        return response.data
    }

    useEffect(() => {
        if (!paramsToSearch.startDate || !paramsToSearch.endDate) return

        setLoadingFetchBankDeposits(true)

        fetchData()
            .then((resp) => {
                setDataBankDeposits(resp?.rows || [])
                setLoadingFetchBankDeposits(false)
            })
            .catch((e) => {
                console.log(e, '________ERROR')
                setLoadingFetchBankDeposits(false)
            })
    }, [execute])

    return {
        dataBankDeposits,
        loadingFetchBankDeposits,
        setParamsToSearch,
        executeFetch,
        resetFetch
    }
}

export const useFetchAllBanks = ({ marvarApi, dispatch, onLogout }) => {

    const [banks, setBanks] = useState([])
    const [loadingFetchAllBanks, setLoadingFetchAllBanks] = useState(false)

    const [execute, setExecute] = useState(false)
    const executeFetch = async () => setExecute(!execute)

    const resetFetch = async () => {
        setBanks([])
    }

    const fetchData = async () => {
        const token = localStorage.getItem('token')
        if (!token) return dispatch(onLogout())

        try {
            const response = await marvarApi.get(`/banco`)
            return response.data
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        setLoadingFetchAllBanks(true)
        fetchData()
            .then((resp) => {
                setBanks(resp?.data || resp || [])
                setLoadingFetchAllBanks(false)
            })
            .catch((e) => {
                console.log(e, '________ERROR')
                setLoadingFetchAllBanks(false)
            })
    }, [execute])

    return { banks, loadingFetchAllBanks, executeFetch, resetFetch }
}

export const useFetchBankDepositsReport = ({ marvarApi, dispatch, onLogout, Helpers, banks }) => {
    const defaultParams = {
        startDate: '',
        endDate: '',
        idbanco: 'ALL',
    }

    const [paramsToSearch, setParamsToSearch] = useState(defaultParams)
    const [dataBankDepositsReport, setDataBankDepositsReport] = useState([])
    const [loadingFetchBankDepositsReport, setLoadingFetchBankDepositsReport] = useState(false)

    const [execute, setExecute] = useState(false)
    const executeFetch = async () => setExecute(prev => !prev)

    const resetFetch = async () => {
        setParamsToSearch(defaultParams)
        setDataBankDepositsReport([])
    }

    const fetchReportData = async () => {
        const token = localStorage.getItem('token')
        if (!token) return dispatch(onLogout())

        const _dataBody = {
            startDate: paramsToSearch.startDate,
            endDate: paramsToSearch.endDate,
            idbanco: (paramsToSearch.idbanco === 'ALL' || !paramsToSearch.idbanco)
                ? 'ALL'
                : paramsToSearch.idbanco
        }

        const response = await marvarApi.post(`/bank/deposits/fetch-transactions-report`, _dataBody)
        return response.data
    }

    const fetchBanksIfNeeded = async () => {
        if (Array.isArray(banks) && banks.length > 0) return banks

        const token = localStorage.getItem('token')
        if (!token) return dispatch(onLogout())

        try {
            const resp = await marvarApi.get(`/banco`)
            return resp?.data?.data || resp?.data || []
        } catch (e) {
            console.log('Error fetching banks:', e)
            return []
        }
    }

    const buildBankMap = (banksArr) => {
        const arr = Array.isArray(banksArr) ? banksArr : []
        return new Map(
            arr.map(b => [
                Number(b.idbanco),
                (b.nombre ?? b.descripcion ?? '').toString()
            ])
        )
    }

    useEffect(() => {
        if (!paramsToSearch.startDate || !paramsToSearch.endDate) return

        setLoadingFetchBankDepositsReport(true)

        Promise.all([fetchReportData(), fetchBanksIfNeeded()])
            .then(([resp, banksArr]) => {
                const rows = resp?.rows || []
                setDataBankDepositsReport(rows)

                const report = rows?.[0]
                if (!report) {
                    setLoadingFetchBankDepositsReport(false)
                    return
                }

                const bankMap = buildBankMap(banksArr)

                const selectedBankName =
                    paramsToSearch.idbanco === 'ALL'
                        ? 'TODOS LOS BANCOS'
                        : bankMap.get(Number(paramsToSearch.idbanco)) || report.banco

                const enrichedData = {
                    ...report,
                    banco: selectedBankName,
                    deposits: (report.deposits || []).map(r => ({
                        ...r,
                        banco: bankMap.get(Number(r.idbanco)) || 'SIN BANCO'
                    }))
                }

                if (Helpers?.drawBankDepositsReport) {
                    Helpers.drawBankDepositsReport(enrichedData)
                }

                setLoadingFetchBankDepositsReport(false)
            })
            .catch((e) => {
                console.log(e, '________ERROR')
                setLoadingFetchBankDepositsReport(false)
            })
    }, [execute, banks])

    return {
        dataBankDepositsReport,
        loadingFetchBankDepositsReport,
        setParamsToSearch,
        executeFetch,
        resetFetch
    }
}

export const useFetchBankMovements = ({ marvarApi, dispatch, onLogout }) => {

    const defaultParams = {
        startDate: '',
        endDate: '',
        idbanco: 'ALL',
        movimiento_tipo: 'ALL',
    }

    const [paramsToSearch, setParamsToSearch] = useState(defaultParams)
    const [dataBankMovements, setDataBankMovements] = useState([])
    const [summaryBankMovements, setSummaryBankMovements] = useState(null)
    const [loadingFetchBankMovements, setLoadingFetchBankMovements] = useState(false)

    const [execute, setExecute] = useState(false)
    const executeFetch = async () => setExecute(prev => !prev)

    const resetFetch = async () => {
        setParamsToSearch(defaultParams)
        setDataBankMovements([])
        setSummaryBankMovements(null)
    }

    const fetchData = async () => {
        const token = localStorage.getItem('token')
        if (!token) return dispatch(onLogout())

        const response = await marvarApi.post(`/bank/movements`, paramsToSearch)
        return response.data
    }

    useEffect(() => {
        if (!paramsToSearch.startDate || !paramsToSearch.endDate) return

        setLoadingFetchBankMovements(true)

        fetchData()
            .then((resp) => {
                setDataBankMovements(resp?.rows || [])
                setSummaryBankMovements(resp?.summary || null)
                setLoadingFetchBankMovements(false)
            })
            .catch((e) => {
                console.log(e, '________ERROR')
                setLoadingFetchBankMovements(false)
            })
    }, [execute])

    return {
        dataBankMovements,
        summaryBankMovements,
        loadingFetchBankMovements,
        setParamsToSearch,
        executeFetch,
        resetFetch
    }
}

export const useFetchBankMovementsReport = ({ marvarApi, dispatch, onLogout, Helpers }) => {
    const defaultParams = {
        startDate: '',
        endDate: '',
        idbanco: 'ALL',
        movimiento_tipo: 'ALL',
    }
    const defaultBase64 = { docName: '', docBase64: '' }

    const [paramsToSearch, setParamsToSearch] = useState(defaultParams)
    const [bankMovementsReportBase64, setBankMovementsReportBase64] = useState(defaultBase64)
    const [loadingFetchBankMovementsReport, setLoadingFetchBankMovementsReport] = useState(false)

    const [execute, setExecute] = useState(false)
    const executeFetch = async () => setExecute(prev => !prev)

    const resetFetch = async () => {
        setParamsToSearch(defaultParams)
        setBankMovementsReportBase64(defaultBase64)
    }

    const fetchData = async () => {
        const token = localStorage.getItem('token')
        if (!token) return dispatch(onLogout())

        const response = await marvarApi.post(`/bank/movements`, paramsToSearch)
        return response.data
    }

    const fetchBankName = async () => {
        if (paramsToSearch.idbanco === 'ALL') return 'TODOS LOS BANCOS'
        try {
            const resp = await marvarApi.get(`/banco`)
            const list = resp?.data?.data || resp?.data || []
            const bank = list.find(b => Number(b.idbanco) === Number(paramsToSearch.idbanco))
            return bank?.nombre || ''
        } catch (e) {
            return ''
        }
    }

    useEffect(() => {
        if (!paramsToSearch.startDate || !paramsToSearch.endDate) return

        setBankMovementsReportBase64(defaultBase64)
        setLoadingFetchBankMovementsReport(true)

        Promise.all([fetchData(), fetchBankName()])
            .then(([resp, bankName]) => {
                const report = Helpers.drawBankMovementsReport({
                    banco: bankName,
                    todosLosBancos: paramsToSearch.idbanco === 'ALL',
                    fecha_inicio: paramsToSearch.startDate,
                    fecha_fin: paramsToSearch.endDate,
                    movimiento_tipo: paramsToSearch.movimiento_tipo,
                    movements: resp?.rows || [],
                    summary: resp?.summary || null,
                })
                setBankMovementsReportBase64(report)
                setLoadingFetchBankMovementsReport(false)
            })
            .catch((e) => {
                console.log(e, '________ERROR')
                setLoadingFetchBankMovementsReport(false)
            })
    }, [execute])

    return { bankMovementsReportBase64, loadingFetchBankMovementsReport, setParamsToSearch, executeFetch, resetFetch }
}
